import { memo } from 'react';

import { ImageUpIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Spinner } from '@air/primitive-spinner';

import {
  SearchCommandVisualSearchWell,
  type SearchCommandVisualSearchWellAppearance,
} from '~/components/SearchCommand/SearchCommandVisualSearchWell';
import { useVisualSearchDropzone } from '~/components/SearchCommand/useVisualSearchDropzone';

export type SearchCommandVisualSearchDropzoneProps = {
  appearance?: SearchCommandVisualSearchWellAppearance;
  onSearch: (base64Image: string) => void;
};

export const SearchCommandVisualSearchDropzone = memo(
  ({ appearance = 'outlined', onSearch }: SearchCommandVisualSearchDropzoneProps) => {
    const { getInputProps, getRootProps, isDragActive, isProcessing, openVisualSearchFilePicker } =
      useVisualSearchDropzone({
        errorContext: 'SearchCommandVisualSearchDropzone: Image processing failed',
        onSearch,
      });

    return (
      <SearchCommandVisualSearchWell
        {...getRootProps()}
        appearance={appearance}
        isDragActive={isDragActive}
        isProcessing={isProcessing}
      >
        <input {...getInputProps()} className="hidden" data-testid="SEARCH_COMMAND_VISUAL_SEARCH_FILE_INPUT" />
        <div className="flex flex-col items-center gap-2 text-center">
          {isProcessing ? (
            <Spinner className="size-6 text-blue-9" />
          ) : (
            <ImageUpIcon className="ads:text-icon-emphasis text-gray-12" size="lg" />
          )}
          <p className="font-medium ads:text-primary text-14 text-gray-12">
            {isDragActive ? 'Drop here to search' : 'Drag and drop image here to search'}
          </p>
          <p className="ads:text-metadata text-12 text-gray-10">
            Upload a JPG or PNG to search for visually similar assets.
          </p>

          <Button
            appearance="outline"
            color="blue"
            data-testid="SEARCH_COMMAND_VISUAL_SEARCH_BROWSE_FILES"
            disabled={isProcessing}
            onClick={openVisualSearchFilePicker}
            size="small"
            type="button"
          >
            Browse files
          </Button>
        </div>
      </SearchCommandVisualSearchWell>
    );
  },
);

SearchCommandVisualSearchDropzone.displayName = 'SearchCommandVisualSearchDropzone';
